// Copyright (c) 2026 Goffy. All Rights Reserved.

// A stable id for this device/browser, sent on hole.view so the device can recognise the echo
// of its own navigation in round.position.updated and only follow OTHER devices. Persisted the
// same way as the round cache (localStorage on web, AsyncStorage on native); until the stored
// value is read a fresh random id is used, which only matters for the first few milliseconds.

type AsyncStorageLike = {
  getItem: (key: string) => Promise<string | null>;
  setItem: (key: string, value: string) => Promise<void>;
};

const STORAGE_KEY = 'goffy:deviceId';

let asyncStorage: AsyncStorageLike | null = null;
try {
  asyncStorage = require('@react-native-async-storage/async-storage').default as AsyncStorageLike;
} catch {
  asyncStorage = null;
}

const getLocalStorage = () => (typeof window !== 'undefined' ? window.localStorage : null);

const randomId = (): string => {
  const cryptoObj = typeof globalThis !== 'undefined' ? (globalThis as { crypto?: Crypto }).crypto : undefined;
  if (cryptoObj?.randomUUID) {
    return cryptoObj.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
};

let deviceId: string = randomId();
let hydrated = false;

const hydrate = async (): Promise<void> => {
  if (hydrated) return;
  hydrated = true;
  try {
    const local = getLocalStorage();
    if (local) {
      const stored = local.getItem(STORAGE_KEY);
      if (stored) {
        deviceId = stored;
      } else {
        local.setItem(STORAGE_KEY, deviceId);
      }
      return;
    }
    if (asyncStorage) {
      const stored = await asyncStorage.getItem(STORAGE_KEY);
      if (stored) {
        deviceId = stored;
      } else {
        await asyncStorage.setItem(STORAGE_KEY, deviceId);
      }
    }
  } catch {
    // Storage is best effort: an unpersisted id still works for the life of this process.
  }
};

void hydrate();

/** The id of this device/browser; stable across launches once storage has been read. */
export const getDeviceId = (): string => deviceId;

/** Test seam. */
export const __setDeviceIdForTests = (value: string): void => {
  deviceId = value;
};
